import Grid from '@mui/material/Grid2';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import SvgIcon from '@mui/material/SvgIcon';
import { useTheme } from '@mui/material';
import { useMemo, useState } from 'react';
import Card from '@mui/material/Card';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { useArticles } from '../hooks';
import { useAppDispatch, useAppSelector } from '../../../hooks/useAppStore';
import { commonActions } from '../../../store/common/slice';
import { TMouseEvent, TSelectedArticle } from '../../../types';
import { helpActions } from '../../../store/help/slice';
import { getSearchArticle, getSelectedArticle } from '../../../store/help/selectors';
import { ReactComponent as PlayIcon } from '../../../static/icons/ic_play.svg';
import { VideoModal } from '../../../components';

const SupportArticles = () => {
  const { articleItems } = useArticles();
  const selectedArticle = useAppSelector(getSelectedArticle);
  const searchArticle = useAppSelector(getSearchArticle);

  const dispatch = useAppDispatch();
  const theme = useTheme() as TCustomTheme;
  const { isLight, isMobile } = useCustomTheme(theme);

  const [isOpenVideoDialog, setIsOpenVideoDialog] = useState(false);
  const [videoTitle, setVideoTitle] = useState('');
  const [videoLink, setVideoLink] = useState('');

  const getArticlesFiltered = useMemo(() => {
    if (!searchArticle) {
      return articleItems;
    }
    return articleItems.filter((article) => article.title.toLowerCase().includes(searchArticle.toLowerCase()));
  }, [articleItems, searchArticle]);

  const onVideoClicked = (e: TMouseEvent, article: TSelectedArticle) => {
    e.stopPropagation();
    setIsOpenVideoDialog(true);
    setVideoTitle(article.title);
    setVideoLink(article.video || '');
  };

  const openArticleNav = (article: TSelectedArticle) => {
    dispatch(helpActions.setSelectedArticle({
      title: article.title,
      articleId: article.articleId,
    }));
    dispatch(commonActions.setIsOpenPagesNav(true));
  };

  return (
    <Grid mt={isMobile ? 4 : 8} container spacing={3}>
      { getArticlesFiltered?.map((article) => (
        <Grid key={article.articleId} size={{ md: 4, lg: 4, xs: 12 }}>
          <Card
            className="align-center cursor-pointer"
            onClick={() => openArticleNav(article)}
            component={Box}
            sx={{
              px: 2,
              position: 'relative',
              border: selectedArticle.articleId === article.articleId ? '1px solid #A855F7' : '',
              boxShadow: isLight ? '' : '0 1px 2px 0 rgba(0, 0, 0, 0.24)',
              '&:hover': {
                boxShadow: isLight
                  ? 'rgba(32, 40, 45, 0.08) 0px 2px 14px 0px'
                  : '0 0 2px 0 rgba(0, 0, 0, 0.24), 0 16px 32px -4px rgba(0, 0, 0, 0.24)',
              },
            }}
          >
            {article.icon && <SvgIcon color="primary" component={article.icon} fontSize="large" inheritViewBox />}
            <Box width="100%">
              <Typography
                variant="body1"
                sx={{
                  py: 3,
                  pl: 3,
                  transition: (theme) => theme.transitions.create('opacity', {
                    duration: theme.transitions.duration.shortest,
                  }),
                }}
              >
                {article.title}
              </Typography>
            </Box>
            <Box onClick={(e) => onVideoClicked(e, article)}>
              <SvgIcon color="primary" component={PlayIcon} fontSize="large" inheritViewBox />
            </Box>
          </Card>
        </Grid>
      )) }
      <VideoModal videoLink={videoLink} videoTitle={videoTitle} isOpenModal={isOpenVideoDialog} setIsOpenModal={setIsOpenVideoDialog} />
    </Grid>
  );
};

export {
  SupportArticles,
};
